/**
 * Read Session API: GET /api/v1/me.
 *
 * Returns the caller's own session summary and nothing else. A 401 means there
 * is no valid session and the caller must clear private state; this function
 * never fabricates a subject and never carries credential material.
 */
import { IdentityRequestError, toRequestError } from "./requestError";

export interface SessionSummary {
  userId: string;
  publicId: string;
  displayName: string;
  status: "ACTIVE" | "SUSPENDED" | "DELETED";
  promotionStatus: "ENABLED" | "SUSPENDED";
  authStrength: "PASSWORD" | "PASSWORD_MFA" | "REMEMBERED";
  expiresAt: string;
}

/**
 * Reads the current session summary. Throws IdentityRequestError on 401/403 so
 * the caller can clear private state and route to sign-in.
 */
export async function readSession(signal?: AbortSignal): Promise<SessionSummary> {
  const response = await fetch("/api/v1/me", {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal,
  });

  if (!response.ok) {
    throw await toRequestError(response);
  }

  return (await response.json()) as SessionSummary;
}

export { IdentityRequestError };
